import React, { useMemo } from 'react';
import { set, startOfDay, addMilliseconds, addDays, subMilliseconds } from 'date-fns';
import {
  constraintAnd,
  constraintNot,
  constraintDisabledTimes,
  constraintIncrementMinute,
  constraintDateDiscreteChecker,
  constraintMin,
  constraintMax,
  constraintMaxDuration,
  constraintEnforceContinuous,
} from './constraints';
import { DatePickerV2 } from './DatePickerV2';
import { CalendarConstraint, CalendarInputTarget, CalendarItem, CalendarPresetItem, DatePickerUnit } from './types';
import { DateRangePickerProps } from '../types';

export type { DateRangePickerProps };

/**
 * DatePickerV2를 DateRangePicker와 동일한 props로 호환시키기 위한 컴포넌트입니다.
 */
export const DateRangePickerCompat: React.FC<DateRangePickerProps> = ({
  width,
  flex,
  size,
  status,
  disabled: disabledProp,
  className,
  hasRemoveButton,
  onOpen,
  onClose,
  zIndex,
  displayMode,
  hasDatePreset,
  hasRadioGroupDatePreset,
  hasTodayButton,
  dates,
  minDate,
  maxDate,
  maxRange,
  customDatePreset,
  disabledDays,
  allowDisabledDaysInRange,
  onChange,
  autoCloseable = true,
  displayTimeFormat,
  hasTimePicker,
  timePickerProps,
  numberOfMonths = displayMode === 'bottom-sheet' || hasTimePicker ? 1 : 2,
  tolerateInvalidValues = true,
  // disabledNowButton,
}) => {
  const unit = useMemo((): DatePickerUnit => {
    if (hasTimePicker) {
      return 'second';
    }
    return 'day';
  }, [hasTimePicker]);
  const format = useMemo((): string | undefined => {
    if (displayTimeFormat === 'hourMinute') {
      return 'yyyy.MM.dd HH:mm';
    }
    if (displayTimeFormat === 'hourMinuteSecond') {
      return 'yyyy.MM.dd HH:mm:ss';
    }
    return undefined;
  }, [displayTimeFormat]);
  const constraint = useMemo((): CalendarConstraint => {
    const getDisabledDaysConstraint = (
      disabledDays: (date: Date) => boolean,
      allowInRange?: boolean,
    ): CalendarConstraint | undefined => {
      const base = constraintNot(constraintDateDiscreteChecker(disabledDays));
      return allowInRange ? base : constraintEnforceContinuous(base);
    };
    const parts = [
      disabledDays && getDisabledDaysConstraint(disabledDays, allowDisabledDaysInRange),
      minDate && constraintMin(startOfDay(minDate)),
      maxDate && constraintMax(addDays(startOfDay(maxDate), 1)),
      // 기존 range의 산정 기준에서 시간은 포함하지 않아서 "11-01 ~ 11-02"을 1일로 인식했으나,
      // 신규 DatePicker는 하루 = 24시간 = 1일로 인식하기 때문에 1을 입력하면 하루밖에 선택할 수 없어서,
      // off by 1 오류를 해소하기 위해 1일을 더합니다.
      maxRange &&
        constraintMaxDuration({ [maxRange[1]]: maxRange[0], days: maxRange[1] === 'days' ? maxRange[0] + 1 : 1 }),
      timePickerProps?.disabledTime && constraintDisabledTimes(timePickerProps.disabledTime),
      timePickerProps?.incrementMinute && constraintIncrementMinute(timePickerProps.incrementMinute),
    ].filter(Boolean) as CalendarConstraint[];

    return constraintAnd(...parts);
  }, [
    allowDisabledDaysInRange,
    disabledDays,
    maxDate,
    maxRange,
    minDate,
    timePickerProps?.disabledTime,
    timePickerProps?.incrementMinute,
  ]);
  const defaultOffset = useMemo((): [Date, Date] | undefined => {
    const defaultTime = timePickerProps?.defaultTime;
    if (defaultTime == null) {
      return undefined;
    }
    const startDate = set(new Date(), {
      hours: defaultTime?.start.hour ?? 0,
      minutes: defaultTime?.start.minute ?? 0,
      seconds: 0,
      milliseconds: 0,
    });
    // 기존 동작에서 defaultTime을 지정하지 않으면 00:00:59.999로 설정되는 문제가 있어서 그대로 재현
    const endDate = addMilliseconds(
      set(new Date(), {
        hours: defaultTime?.end.hour ?? 0,
        minutes: defaultTime?.end.minute ?? 0,
        seconds: 59,
        milliseconds: 999,
      }),
      1,
    );
    return [startDate, endDate];
  }, [timePickerProps?.defaultTime]);
  const disabled = useMemo((): boolean => {
    if (Array.isArray(disabledProp)) {
      return disabledProp[0] && disabledProp[1];
    }
    return !!disabledProp;
  }, [disabledProp]);
  const target = useMemo((): CalendarInputTarget | undefined => {
    if (!Array.isArray(disabledProp)) {
      return undefined;
    }
    if (disabledProp[0] && !disabledProp[1]) {
      return 'to';
    }
    if (!disabledProp[0] && disabledProp[1]) {
      return 'from';
    }
    return undefined;
  }, [disabledProp]);
  const presets = useMemo((): CalendarPresetItem[] | undefined => {
    if (customDatePreset == null) {
      return undefined;
    }
    return customDatePreset.map(({ label, range }) => ({
      label,
      interval: { from: range[0], to: range[1] },
    }));
  }, [customDatePreset]);
  const calendarValue = useMemo(() => {
    if (dates?.from && dates?.to) {
      return [{ id: '', from: dates.from, to: addMilliseconds(dates.to, 1) }];
    }
    return [];
  }, [dates?.from, dates?.to]);
  const defaultCursorItem = useMemo<Partial<CalendarItem> | null>(() => {
    if (dates?.from && !dates?.to) {
      return { from: dates.from };
    }
    if (!dates?.from && dates?.to) {
      return { to: addMilliseconds(dates.to, 1) };
    }
    return null;
  }, [dates?.from, dates?.to]);
  return (
    <DatePickerV2
      width={width}
      flex={flex}
      size={size}
      status={status}
      disabled={disabled}
      className={className}
      hasRemoveButton={hasRemoveButton}
      zIndex={zIndex}
      displayMode={displayMode}
      numberOfMonths={numberOfMonths}
      constraint={constraint}
      hasTodayButton={hasTodayButton}
      hasControls={!autoCloseable}
      hasDatePreset={hasDatePreset}
      hasRadioGroupDatePreset={hasRadioGroupDatePreset}
      onOpen={onOpen}
      onClose={onClose}
      unit={unit}
      format={format}
      target={target}
      value={calendarValue}
      defaultCursorItem={defaultCursorItem}
      onChange={(items) => {
        onChange?.(
          target === 'to' ? dates?.from : items[0]?.from,
          target === 'from' ? dates?.to : items[0]?.to ? subMilliseconds(items[0].to, 1) : undefined,
        );
      }}
      defaultOffset={defaultOffset}
      presets={presets}
      tolerateInvalidValues={tolerateInvalidValues}
    />
  );
};

export const DateRangePicker = DateRangePickerCompat;
